import React, { useState } from 'react';
import { ADMIN_ROLE } from '../utils/roles';
import { downloadAgentFile, deleteAgentFile, fetchProfile } from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';
import { formatDate, getDateFormat } from '../utils/date';
import UploadAgentFileModal from './UploadAgentFileModal';
import './_Buttons.css';
import './_UserTiles.css';
import './StudentDetail.css';

const AgentDetailModal = ({ agent, onClose, onEdit, onDelete, canManage, onAgentUpdate }) => {
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [profile, setProfile] = useState(null);
    const [dateFormat, setDateFormat] = useState(getDateFormat());

    React.useEffect(() => {
        const getProfile = async () => {
            try {
                const profileData = await fetchProfile();
                setProfile(profileData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };
        getProfile();
    }, []);

    React.useEffect(() => {
        const handleDateFormatChange = () => {
            setDateFormat(getDateFormat());
        };

        window.addEventListener('dateFormatChanged', handleDateFormatChange);

        return () => {
            window.removeEventListener('dateFormatChanged', handleDateFormatChange);
        };
    }, []);

    if (!agent) {
        return null;
    }

    const handleFileUploaded = () => {
        onAgentUpdate();
    };

    const handleDownloadFile = async (fileId) => {
        try {
            await downloadAgentFile(fileId);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        try {
            await deleteAgentFile(fileId);
            onAgentUpdate();
            setMessage('File deleted successfully.');
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const canDeleteFile = (file) => {
        if (!profile) return false;
        return profile.id === file.created_by.id || profile.roles.some(role => role.id === ADMIN_ROLE);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>X</button>
                <h2>{agent.name}</h2>
                {agent.email && <p><strong>Email:</strong> {agent.email}</p>}
                {agent.phone && <p><strong>Phone:</strong> {agent.phone}</p>}
                <p><strong>Contact Name:</strong> {agent.contact_name}</p>
                {agent.description && <p><strong>Description:</strong> {agent.description}</p>}
                <p><strong>Agreement Start Date:</strong> {formatDate(agent.agreement_start_date)}</p>
                {agent.agreement_end_date && <p><strong>Agreement End Date:</strong> {formatDate(agent.agreement_end_date)}</p>}

                <div className="files-section-agent">
                    <div className="files-header">
                        <h3>Files</h3>
                        {canManage && <button onClick={() => setUploadModalOpen(true)} className="submit-button text-button">Upload File</button>}
                    </div>
                    {agent.files && agent.files.length > 0 ? (
                        <div className="files-list">
                            {agent.files.map(file => (
                                <div key={file.id} className="file-card">
                                    <div className="file-card-header">
                                        <span className="file-type">{file.type}</span>
                                        <div className="file-actions">
                                            <button onClick={() => handleDownloadFile(file.id)} className="file-action-button">⬇️</button>
                                            {canDeleteFile(file) && (
                                                <button onClick={() => handleDeleteFile(file.id)} className="file-action-button">🗑️</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="file-meta">
                                        <span>{`${file.created_by.first_name} ${file.created_by.last_name}`}</span>
                                        <span>{formatDate(file.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>No files found for this agent.</p>
                    )}
                </div>

                <div className='actions-centered'>
                    {canManage && <button onClick={() => onEdit(agent)} className="cancel-button text-button">Edit</button>}
                    {canManage && <button onClick={() => onDelete(agent.id)} className="submit-button text-button">Delete</button>}
                </div>
                {isUploadModalOpen && (
                    <UploadAgentFileModal
                        agentId={agent.id}
                        onClose={() => setUploadModalOpen(false)}
                        onFileUploaded={handleFileUploaded}
                    />
                )}
                 {error && <p className="error">{error}</p>}
                {message && <p className="message">{message}</p>}
            </div>
        </div>
    );
};

export default AgentDetailModal;